import { DateTime } from 'luxon'
import { post } from './apiHelper.ts'
import { DAY_HISTORY_URL } from './urls.ts'
import {
    IDayHistoryResponse,
    IDayHistoryWaypoint,
    IDayHistoryGap,
    InterpolatedPosition,
} from '../interfaces/dayHistory.ts'

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Maximum real-time seconds between consecutive waypoints before the client
 * subdivides the segment during interpolation.
 * Set to 0 to disable subdivision (trust server smoothing only).
 */
export const MAX_SEGMENT_DURATION = 120 // seconds

/**
 * Maximum zoom out level for initial path fit.
 * The map won't zoom out further than this when fitting the path to the viewport.
 */
export const MAX_ZOOM_OUT = 12

// ---------------------------------------------------------------------------
// Coordinate conversion
// ---------------------------------------------------------------------------

/**
 * Convert [lng, lat] (GeoJSON order) to Google Maps LatLngLiteral.
 * Use this everywhere instead of manually constructing LatLng objects.
 */
export const toLatLng = (coord: [number, number]): google.maps.LatLngLiteral => ({
    lat: coord[1],
    lng: coord[0],
})

/**
 * Convert a waypoint to Google Maps LatLngLiteral.
 */
export const waypointToLatLng = (wp: IDayHistoryWaypoint): google.maps.LatLngLiteral => ({
    lat: wp.lat,
    lng: wp.lng,
})

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

/**
 * Fetch day history path data for a pet/device on a given day.
 *
 * @param iccid - Device ICCID
 * @param petId - Pet ID
 * @param date - The day to fetch, as a Luxon DateTime in the user's timezone
 */
export const fetchDayHistory = async (
    iccid: string,
    petId: string,
    date: DateTime
): Promise<IDayHistoryResponse | null> => {
    // date must already be in the user's timezone.
    // Compute day boundaries in that timezone, then convert to UTC epoch seconds.
    const startOfDay = date.startOf('day')
    const endOfDay = date.endOf('day')

    const body = {
        iccid,
        petId,
        startTime: Math.floor(startOfDay.toSeconds()),
        endTime: Math.floor(endOfDay.toSeconds()),
    }

    try {
        const response = await post(DAY_HISTORY_URL, body)
        if (response?.status === 200 && response.data) {
            return response.data as IDayHistoryResponse
        }
        return null
    } catch {
        return null
    }
}

// ---------------------------------------------------------------------------
// Interpolation
// ---------------------------------------------------------------------------

/**
 * Given a sorted path and a currentTime (epoch seconds), find the interpolated
 * position of the pet marker.
 *
 * Returns the interpolated lat/lng, gap status, and any event at this time.
 */
export const interpolatePosition = (
    path: IDayHistoryWaypoint[],
    gaps: IDayHistoryGap[],
    currentTime: number
): InterpolatedPosition => {
    // Check if we're in a gap first
    const activeGap = gaps.find(g => currentTime >= g.startTime && currentTime <= g.endTime)

    if (activeGap) {
        // During a gap, find the last known position before the gap
        const lastBefore = findLastWaypointBefore(path, activeGap.startTime)
        const pos = lastBefore
            ? { lat: lastBefore.lat, lng: lastBefore.lng }
            : path.length > 0
                ? { lat: path[0].lat, lng: path[0].lng }
                : { lat: 0, lng: 0 }

        return {
            ...pos,
            time: currentTime,
            inGap: true,
            gapReason: activeGap.reason,
        }
    }

    // No path data
    if (path.length === 0) {
        return { lat: 0, lng: 0, time: currentTime, inGap: true, gapReason: 'no_data' }
    }

    // Before first waypoint
    if (currentTime <= path[0].time) {
        return {
            lat: path[0].lat,
            lng: path[0].lng,
            time: currentTime,
            inGap: false,
            event: path[0].event,
        }
    }

    // After last waypoint
    if (currentTime >= path[path.length - 1].time) {
        const last = path[path.length - 1]
        return {
            lat: last.lat,
            lng: last.lng,
            time: currentTime,
            inGap: false,
            event: last.event,
        }
    }

    // Binary search for the bracketing waypoints
    const { before, after } = findBracketingWaypoints(path, currentTime)

    // Exact match on a waypoint
    if (before.time === currentTime) {
        return {
            lat: before.lat,
            lng: before.lng,
            time: currentTime,
            inGap: false,
            event: before.event,
        }
    }

    // Linear interpolation between before and after
    const segmentDuration = after.time - before.time
    const elapsed = currentTime - before.time
    const t = segmentDuration > 0 ? elapsed / segmentDuration : 0

    const lat = before.lat + (after.lat - before.lat) * t
    const lng = before.lng + (after.lng - before.lng) * t

    return {
        lat,
        lng,
        time: currentTime,
        inGap: false,
        event: null,
    }
}

/**
 * Find the two waypoints that bracket a given time.
 * Uses binary search for efficiency.
 */
export const findBracketingWaypoints = (
    path: IDayHistoryWaypoint[],
    time: number
): { before: IDayHistoryWaypoint; after: IDayHistoryWaypoint } => {
    let lo = 0
    let hi = path.length - 1

    while (lo < hi - 1) {
        const mid = Math.floor((lo + hi) / 2)
        if (path[mid].time <= time) {
            lo = mid
        } else {
            hi = mid
        }
    }

    return { before: path[lo], after: path[hi] }
}

/**
 * Find the last waypoint at or before a given time.
 */
export const findLastWaypointBefore = (
    path: IDayHistoryWaypoint[],
    time: number
): IDayHistoryWaypoint | null => {
    if (path.length === 0) return null

    let lo = 0
    let hi = path.length - 1
    let result: IDayHistoryWaypoint | null = null

    while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2)
        if (path[mid].time <= time) {
            result = path[mid]
            lo = mid + 1
        } else {
            hi = mid - 1
        }
    }

    return result
}

// ---------------------------------------------------------------------------
// Segment subdivision (configurable density)
// ---------------------------------------------------------------------------

/**
 * If the time gap between two consecutive waypoints exceeds MAX_SEGMENT_DURATION,
 * generate intermediate interpolated positions so the animation is smooth.
 *
 * Returns an expanded path with subdivided segments inserted.
 * If MAX_SEGMENT_DURATION is 0, returns the original path unchanged.
 */
export const subdividePath = (path: IDayHistoryWaypoint[]): IDayHistoryWaypoint[] => {
    if (MAX_SEGMENT_DURATION <= 0 || path.length < 2) return path

    const result: IDayHistoryWaypoint[] = [path[0]]

    for (let i = 1; i < path.length; i++) {
        const prev = path[i - 1]
        const curr = path[i]
        const duration = curr.time - prev.time

        if (duration > MAX_SEGMENT_DURATION) {
            // Subdivide this segment
            const numSegments = Math.ceil(duration / MAX_SEGMENT_DURATION)
            const dt = duration / numSegments
            const dlat = (curr.lat - prev.lat) / numSegments
            const dlng = (curr.lng - prev.lng) / numSegments

            for (let j = 1; j < numSegments; j++) {
                result.push({
                    lat: prev.lat + dlat * j,
                    lng: prev.lng + dlng * j,
                    time: Math.round(prev.time + dt * j),
                    accuracy: Math.max(prev.accuracy, curr.accuracy),
                    event: null,
                    speed: null,
                })
            }
        }

        result.push(curr)
    }

    return result
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/**
 * Convert the smoothPath ([lng, lat, time][] | null) to Google Maps LatLngLiteral[].
 */
export const smoothPathToLatLngs = (
    smoothPath: [number, number, number][] | null
): google.maps.LatLngLiteral[] => {
    if (!smoothPath) return []
    return smoothPath.map(coord => ({ lat: coord[1], lng: coord[0] }))
}

/**
 * Convert timed smoothPath to a waypoint array suitable for interpolation.
 * Each entry has lat, lng, time — the same shape the interpolation engine expects.
 */
export const smoothPathToWaypoints = (
    smoothPath: [number, number, number][] | null
): IDayHistoryWaypoint[] => {
    if (!smoothPath) return []
    return smoothPath.map(coord => ({
        lat: coord[1],
        lng: coord[0],
        time: coord[2],
        accuracy: 0,
        event: null,
        speed: null,
    }))
}

/**
 * Compute the progress through the day (0-1) for timeline rendering.
 */
export const getProgress = (currentTime: number, startTime: number, endTime: number): number => {
    if (endTime <= startTime) return 0
    return Math.max(0, Math.min(1, (currentTime - startTime) / (endTime - startTime)))
}

/**
 * Compute the next playback speed in the cycle.
 */
export const cycleSpeed = (current: 120 | 360 | 720): 120 | 360 | 720 => {
    switch (current) {
        case 120: return 360
        case 360: return 720
        case 720: return 120
    }
}

/**
 * Format a speed multiplier for display.
 */
export const formatSpeed = (speed: 120 | 360 | 720): string => {
    switch (speed) {
        case 120: return '2 min/s'
        case 360: return '6 min/s'
        case 720: return '12 min/s'
    }
}
